import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, Bot, Receipt } from "lucide-react";
import axios from "axios";
import config from "../../config";
import { DAY, NO_DATA } from "../lib/format";
import { InlineLoader, PageLoader } from "../components/PageLoader";
import { BalanceCard } from "../components/wallet/BalanceCard";
import { ArrowLink, IconChip, RechargeDialog, Toast, TxnRow, glass, paper, useCountUp } from "../components/wallet/parts";
import { apiGet, count, money, money4, monthName, toTxn } from "../components/wallet/api";

/**
 * Wallet (design: screens/Wallet.tsx).
 *
 * Layout, wording and styling are the design's. Amounts are USD, as the wallet is kept.
 *
 * Real data and actions:
 *   balance, recharge limits, total spent ........ GET /api/waba/wallet
 *   spent this month, messages this month ......... GET /api/waba/wallet/summary?period=month
 *   "lasts about N days" .......................... balance ÷ this month's average daily spend
 *   spend in the last 7 days, WhatsApp share ...... GET /api/waba/wallet/summary?period=week
 *   last recharge ................................. GET /api/waba/wallet/transactions?type=recharge&limit=1
 *   recent transactions ........................... GET /api/waba/wallet/transactions?limit=6
 *   WhatsApp message rates ........................ GET /api/waba/pricing
 *   clinic name, billing name / country / GSTIN ... GET /api/organizations/my-organizations
 *   GST rate ...................................... GET /api/payments/calculate-gst
 *   Recharge ...................................... POST /api/payments/initiate → Razorpay Checkout
 *                                                   → POST /api/payments/razorpay/verify
 *   spend per day by channel (chart, totals) ...... GET /api/waba/wallet/spend-daily?days=7|30 (item 122)
 *   change vs the same days last month ............ summary previousPeriodSpent / changePct (123)
 *   AI chats and call minutes this month .......... summary ai (124)
 *   AI agent rates for this clinic ................ GET /api/ai-agent/pricing (125; billed per message)
 *   total spent, net of refunds ................... wallet net_spent (126)
 * Number rental is in each day's total but has no bar of its own, as in the design.
 *   low-balance warning and its threshold ......... wallet low_balance_threshold; PUT /api/waba/wallet/settings
 *                                                   { low_balance_threshold } (old WalletDashboard "Low Balance")
 *   per-country message rates, my subscription ... links to /wallet/pricing and /subscription/manage
 * A wallet that can't be loaded says so with Try again instead of showing dashes.
 */

const reveal = (i) => ({ animationDelay: `${60 + i * 60}ms` });

const CATEGORY = {
  marketing: { label: "Marketing", uses: "Offers and campaigns" },
  utility: { label: "Utility", uses: "Reminders, receipts, follow-ups" },
  authentication: { label: "Authentication", uses: "Login codes" },
};
const CHANNEL = {
  message: { label: "WhatsApp messages", fill: "var(--color-sky)", dot: "bg-sky", bar: "bg-sky" },
  "ai-voice": { label: "AI voice calls", fill: "var(--color-muted)", dot: "bg-muted", bar: "bg-muted" },
  "ai-web": { label: "AI web widget", fill: "var(--color-sky-deep)", dot: "bg-sky-deep", bar: "bg-sky-deep" },
  "ai-whatsapp": { label: "AI on WhatsApp", fill: "var(--color-ink)", dot: "bg-ink", bar: "bg-ink" },
};
/** Bottom to top in the chart */
const CHANNEL_ORDER = ["message", "ai-voice", "ai-web", "ai-whatsapp"];

const numOrNull = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));
/** Count and cost of WhatsApp messages in a summary (its "other" bucket is AI agents and rentals). */
function messagesIn(summary) {
  const cats = summary?.by_category;
  if (!cats) return { count: null, cost: null };
  return Object.entries(cats).reduce(
    (t, [k, v]) => (k === "other" ? t : { count: t.count + (Number(v.count) || 0), cost: t.cost + (Number(v.cost) || 0) }),
    { count: 0, cost: 0 },
  );
}

async function loadWallet() {
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const [w, month, week, recent, lastR, pricing, orgs, gst, aiPricing] = await Promise.all([
    apiGet("/api/waba/wallet"),
    apiGet("/api/waba/wallet/summary?period=month"),
    apiGet("/api/waba/wallet/summary?period=week"),
    apiGet("/api/waba/wallet/transactions?limit=6"),
    apiGet("/api/waba/wallet/transactions?type=recharge&limit=1"),
    apiGet("/api/waba/pricing"),
    apiGet("/api/organizations/my-organizations"),
    apiGet("/api/payments/calculate-gst?amount=100"),
    apiGet("/api/ai-agent/pricing"),
  ]);

  const list = orgs?.success ? orgs.data || [] : [];
  const org = list.find((o) => o.id === user?.organization_id) || list[0] || null;
  const balance = w?.success ? numOrNull(w.wallet?.balance) : null;
  const monthSpent = month?.success ? numOrNull(month.summary?.totalSpent) : null;
  const elapsed = Math.max(1, Math.ceil((Date.now() - new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime()) / DAY));
  const daily = monthSpent === null ? null : monthSpent / elapsed;
  const last = lastR?.success ? lastR.transactions?.[0] : undefined;
  const calc = gst?.success ? gst.calculation : null;
  const weekMsgs = week?.success ? messagesIn(week.summary) : { count: null, cost: null };

  return {
    failed: !w?.success,
    balance,
    threshold: w?.success ? numOrNull(w.wallet?.low_balance_threshold) : null,
    limits: w?.recharge_limits || null,
    // Net of refunds (item 126); the gross figure stands in for an older backend.
    spent: w?.success ? numOrNull(w.wallet?.net_spent ?? w.wallet?.total_spent) : null,
    spentIsNet: w?.success && w.wallet?.net_spent !== undefined,
    refunded: w?.success ? numOrNull(w.wallet?.total_refunded) : null,
    monthChange: month?.success ? numOrNull(month.changePct) : null,
    monthAi: month?.success ? month.ai || null : null,
    aiRates: aiPricing?.success ? aiPricing.rates || null : null,
    monthSpent,
    monthMessages: month?.success ? messagesIn(month.summary).count : null,
    daily,
    daysLeft: balance === null || daily === null ? null : daily > 0 ? Math.floor(balance / daily) : Infinity,
    lastRecharge: last === undefined ? undefined : last ? { amount: Number(last.amount), ts: new Date(last.created_at).getTime() } : null,
    recent: recent?.success ? (recent.transactions || []).map(toTxn) : [],
    week: { all: week?.success ? numOrNull(week.summary?.totalSpent) : null, message: weekMsgs.cost },
    pricing: pricing?.success ? pricing.pricing || [] : null,
    clinic: org?.name || NO_DATA,
    billing: calc
      ? { india: !!calc.isIndian, gstRate: Number(calc.gstRate) || 0, gstin: calc.gstin || org?.gstin || "", legalName: org?.name || "your clinic", country: org?.country || "" }
      : null,
  };
}

function StatCard({ i, icon, label, value, meta, to, toLabel }) {
  const shown = useCountUp(value ?? 0, 900);
  return (
    <section aria-label={label} style={reveal(i)} className={`${glass} col-span-3 flex min-h-[248px] animate-nav-in flex-col justify-between p-5`}>
      <div className="flex items-start justify-between">
        <IconChip>{icon}</IconChip>
        <ArrowLink to={to} label={toLabel} />
      </div>
      <div className="flex flex-col gap-3">
        <span className="text-body font-medium text-ink/80">{label}</span>
        <p className="text-stat font-semibold tabular-nums" aria-label={money(value)}>
          {value === null ? NO_DATA : money(shown)}
        </p>
        <div className="text-caption text-muted">{meta}</div>
      </div>
    </section>
  );
}

function ChartTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="flex min-w-52 flex-col gap-1.5 rounded-md border border-paper/80 bg-paper/95 px-3.5 py-3 text-caption shadow-lift">
      <span className="font-semibold">{row.label}</span>
      {[...CHANNEL_ORDER].reverse().map((c) => (
        <span key={c} className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${CHANNEL[c].dot}`} />
          <span className="flex-1 text-muted">{CHANNEL[c].label}</span>
          <span className="tabular-nums">{money(row[c])}</span>
        </span>
      ))}
      <span className="mt-0.5 flex justify-between border-t border-line pt-1.5 font-semibold">
        <span>Total</span>
        <span className="tabular-nums">{money(row.total)}</span>
      </span>
    </div>
  );
}

// The backend's channel keys → the chart's.
const KEY = { message: "message", ai_voice: "ai-voice", ai_web: "ai-web", ai_whatsapp: "ai-whatsapp" };
function SpendChart() {
  const [days, setDays] = useState(30);
  const [daily, setDaily] = useState(null);
  // Spend per day by channel, net of refunds (item 122).
  useEffect(() => {
    let alive = true;
    setDaily(null);
    apiGet(`/api/waba/wallet/spend-daily?days=${days}`).then((r) => { if (alive) setDaily(r?.success ? r : false); });
    return () => { alive = false; };
  }, [days]);
  const data = useMemo(() => (daily ? daily.days || [] : []).map((d) => ({
    label: new Date(`${d.date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
    ...Object.fromEntries(Object.entries(KEY).map(([from, to]) => [to, Number(d[from]) || 0])),
    total: Number(d.total) || 0,
  })), [daily]);
  const totals = useMemo(() => {
    const t = { all: daily ? Number(daily.totals?.all ?? 0) : null };
    for (const [from, to] of Object.entries(KEY)) t[to] = daily ? Number(daily.totals?.[from] ?? 0) : null;
    return t;
  }, [daily]);
  const share = (c) => (totals[c] === null || !totals.all ? null : totals[c] / totals.all);
  // null while the chosen range loads (a loader), false if the call failed ("—").
  const pending = daily === null;

  return (
    <section aria-label="Spend" style={reveal(3)} className={`${glass} col-span-8 flex animate-nav-in flex-col gap-5 p-6`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-h3 font-semibold">Spend</h2>
          <p className="text-small text-muted">
            <span className="font-semibold tabular-nums text-ink">{pending ? <InlineLoader size={14} /> : money(totals.all)}</span> in the last {days} days, messages and AI agents
          </p>
        </div>
        <div role="tablist" aria-label="Period" className="flex rounded-full bg-paper/70 p-1 ring-1 ring-paper">
          {[7, 30].map((d) => (
            <button
              key={d}
              role="tab"
              aria-selected={days === d}
              onClick={() => setDays(d)}
              className={`h-8 rounded-full px-4 text-small font-medium ${days === d ? "bg-ink text-paper" : "text-muted hover:text-ink"}`}
            >
              {d} days
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-60 flex-1">
        {pending ? <PageLoader label="Loading spend…" className="h-full min-h-60" /> : <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }} barCategoryGap={days === 7 ? "28%" : "22%"}>
            <CartesianGrid vertical={false} stroke="var(--color-paper)" strokeDasharray="3 5" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} interval={days === 7 ? 0 : 4} tick={{ fill: "var(--color-muted)", fontSize: 12 }} dy={6} />
            <YAxis axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `$${v.toFixed(0)}`} tick={{ fill: "var(--color-muted)", fontSize: 12 }} />
            <Tooltip cursor={{ fill: "var(--color-paper)", fillOpacity: 0.6, radius: 8 }} content={<ChartTip />} />
            {CHANNEL_ORDER.map((c, i) => (
              <Bar
                key={c}
                dataKey={c}
                stackId="spend"
                fill={CHANNEL[c].fill}
                radius={i === CHANNEL_ORDER.length - 1 ? [6, 6, 0, 0] : 0}
                animationDuration={700}
                animationBegin={i * 90}
                animationEasing="ease-out"
              />
            ))}
          </BarChart>
        </ResponsiveContainer>}
      </div>

      {/* Where the money went: share of spend by source */}
      <div className="flex flex-col gap-3">
        <div className="flex h-2 overflow-hidden rounded-full bg-paper/70">
          {CHANNEL_ORDER.map((c, i) => (
            <span
              key={`${c}-${days}`}
              aria-hidden
              style={{ width: `${(share(c) || 0) * 100}%`, animationDelay: `${200 + i * 90}ms` }}
              className={`h-full origin-left animate-grow-x ${CHANNEL[c].bar}`}
            />
          ))}
        </div>
        <ul className="grid grid-cols-4 gap-3">
          {CHANNEL_ORDER.map((c) => (
            <li key={c} className="flex flex-col gap-0.5">
              <span className="flex items-center gap-2 text-caption text-muted">
                <span className={`h-2.5 w-2.5 rounded-full ${CHANNEL[c].dot}`} />
                {CHANNEL[c].label}
              </span>
              <span className="text-small font-semibold tabular-nums">
                {pending ? <InlineLoader size={14} /> : <>
                  {money(totals[c])}
                  <span className="ml-1.5 font-normal text-muted">{share(c) === null ? NO_DATA : `${Math.round(share(c) * 100)}%`}</span>
                </>}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function RateRow({ dot, label, uses, rate }) {
  return (
    <li className="flex items-center gap-3 border-t border-paper py-3 first:border-t-0 first:pt-0">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-small font-semibold">{label}</span>
        <span className="truncate text-caption text-muted">{uses}</span>
      </div>
      <span className="text-small font-semibold tabular-nums">{rate}</span>
    </li>
  );
}

function RatesPanel({ pricing, aiRates }) {
  const order = ["marketing", "utility", "authentication"];
  // The clinic's effective rates; India where listed (as the old pricing page defaulted), else the first country.
  const country = pricing?.some((p) => p.country_code === "IN") ? "IN" : pricing?.[0]?.country_code;
  const rows = (pricing || []).filter((p) => p.country_code === country);
  const rateFor = (c) => rows.find((p) => p.category === c)?.final_price;
  const countryName = rows[0]?.country_name;
  return (
    <section aria-labelledby="rates-title" style={reveal(4)} className={`${glass} col-span-4 flex animate-nav-in flex-col gap-4 p-6`}>
      <h2 id="rates-title" className="text-h3 font-semibold">
        Rates
      </h2>
      <div className="flex flex-col gap-2">
        <span className="text-caption font-semibold text-muted">WhatsApp messages{countryName ? ` to ${countryName}` : ""}, per delivered message</span>
        <ul className="flex flex-col">
          {order.map((c) => (
            <RateRow key={c} dot={CHANNEL.message.dot} label={CATEGORY[c].label} uses={CATEGORY[c].uses} rate={money4(rateFor(c))} />
          ))}
          <RateRow dot="border border-muted/60" label="Service" uses="Replies within 24 hours" rate="Free" />
        </ul>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-caption font-semibold text-muted">AI agents</span>
        {/* The clinic's own rates (item 125). AI chats are billed per message, not per conversation. */}
        <ul className="flex flex-col">
          <RateRow dot={CHANNEL["ai-whatsapp"].dot} label="WhatsApp" uses="Per message" rate={aiRates?.whatsapp_per_message == null ? NO_DATA : money4(aiRates.whatsapp_per_message)} />
          <RateRow dot={CHANNEL["ai-web"].dot} label="Web widget" uses="Per message" rate={aiRates?.web_per_message == null ? NO_DATA : money4(aiRates.web_per_message)} />
          <RateRow dot={CHANNEL["ai-voice"].dot} label="Voice, incoming" uses="Per minute" rate={aiRates?.voice_in_per_min == null ? NO_DATA : money4(aiRates.voice_in_per_min)} />
          <RateRow dot={CHANNEL["ai-voice"].dot} label="Voice, outgoing" uses="Per minute" rate={aiRates?.voice_out_per_min == null ? NO_DATA : money4(aiRates.voice_out_per_min)} />
        </ul>
      </div>
      <p className="mt-auto text-caption text-muted">Failed messages are refunded automatically.</p>
    </section>
  );
}

/** "Warn me below $X": the wallet's low-balance threshold, saved with PUT /api/waba/wallet/settings. */
function AlertSetting({ threshold, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const open = () => { setValue(threshold === null ? "" : String(threshold)); setError(""); setEditing(true); };
  const save = async () => {
    const n = Number(value);
    if (value.trim() === "" || !Number.isFinite(n) || n < 0 || n > 100000) { setError("Enter an amount in dollars, 0 or more."); return; }
    setSaving(true); setError("");
    try {
      const r = await axios.put(`${config.API_BASE_URL}/api/waba/wallet/settings`, { low_balance_threshold: Math.round(n * 100) / 100 }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setEditing(false);
      onSaved(numOrNull(r.data?.wallet?.low_balance_threshold) ?? n);
    } catch (e) {
      setError(e.response?.data?.message || "Couldn't save the alert. Try again.");
    } finally { setSaving(false); }
  };
  if (!editing) return <span className="flex items-center gap-2 text-small text-muted">Low-balance alert below <span className="font-semibold tabular-nums text-ink">{threshold === null ? NO_DATA : money(threshold)}</span><button type="button" onClick={open} className="font-semibold text-sky-deep hover:underline">Change</button></span>;
  return <span className="flex flex-wrap items-center gap-2 text-small"><label className="flex h-9 items-center gap-1 rounded-full bg-paper px-3 ring-1 ring-line"><span className="text-muted">$</span><input autoFocus inputMode="decimal" aria-label="Low-balance alert in dollars" value={value} onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ""))} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} className="w-16 bg-transparent tabular-nums outline-none" /></label><button type="button" onClick={save} disabled={saving} className="h-9 rounded-full bg-ink px-4 text-caption font-semibold text-paper disabled:opacity-60">{saving ? "Saving…" : "Save"}</button><button type="button" onClick={() => setEditing(false)} className="h-9 rounded-full px-3 text-caption font-semibold hover:bg-mist">Cancel</button>{error && <span role="alert" className="basis-full text-caption text-danger">{error}</span>}</span>;
}

export default function Wallet() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [toast, setToast] = useState("");
  const [freshSince, setFreshSince] = useState(Infinity);

  const refresh = useCallback(() => loadWallet().then(setData), []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const close = useCallback(() => setDialog(false), []);
  const clearToast = useCallback(() => setToast(""), []);
  const prev = monthName((new Date().getMonth() + 11) % 12);
  const loading = !data;

  return (
    <div className="relative mx-auto flex max-w-7xl flex-col gap-6 px-8 py-9">
      <header className="flex animate-fade-in flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-h1 font-semibold">Wallet</h1>
          <p className="text-body text-muted">Balance for WhatsApp messages and AI agents</p>
        </div>
        <nav aria-label="Wallet pages" className="flex flex-wrap items-center gap-2">
          <Link to="/wallet/pricing" className="flex h-9 items-center rounded-full bg-paper px-4 text-small font-semibold ring-1 ring-line hover:bg-mist">Rates by country</Link>
          <Link to="/subscription/manage" className="flex h-9 items-center rounded-full bg-paper px-4 text-small font-semibold ring-1 ring-line hover:bg-mist">My subscription</Link>
        </nav>
      </header>

      {!loading && !data.failed && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {data.balance !== null && data.threshold !== null && data.balance < data.threshold ? (
            <p role="alert" className="flex items-center gap-2 rounded-full bg-warning-soft px-4 py-2 text-small font-medium text-warning">
              <AlertTriangle size={16} strokeWidth={1.75} />
              Low balance: {money(data.balance)} is below your {money(data.threshold)} alert. Messages stop when it runs out.
              <button type="button" onClick={() => setDialog(true)} className="font-semibold text-ink underline">Recharge now</button>
            </p>
          ) : <span />}
          <AlertSetting threshold={data.threshold} onSaved={(t) => { setData((d) => ({ ...d, threshold: t })); setToast(`Low-balance alert set to ${money(t)}`); }} />
        </div>
      )}

      {loading ? (
        <PageLoader label="Loading wallet…" />
      ) : data.failed ? (
        <section role="alert" className={`${paper} flex flex-col items-center gap-3 px-6 py-14 text-center`}>
          <p className="text-body font-semibold">Couldn't load the wallet</p>
          <p className="text-small text-muted">Check your connection, or ask an admin for access to the wallet.</p>
          <button type="button" onClick={() => { setData(null); refresh(); }} className="h-9 rounded-full px-4 text-caption font-semibold ring-1 ring-line hover:bg-mist">Try again</button>
        </section>
      ) : (
        <div className="relative isolate grid grid-cols-12 gap-6">
          {/* Morning light behind the glass cards, so the glass has colour to frost (brand: glass only over the morning gradient) */}
          <div aria-hidden className="pointer-events-none absolute -inset-x-10 -top-10 bottom-[260px] -z-10">
            <span className="absolute right-[4%] top-[2%] h-[340px] w-[480px] animate-glow-in rounded-full bg-sky/60 blur-3xl" />
            <span className="absolute left-[38%] top-[44%] h-[300px] w-[420px] animate-glow-in rounded-full bg-sky/45 blur-3xl" style={{ animationDelay: "150ms" }} />
            <span className="absolute bottom-[4%] left-[2%] h-[260px] w-[380px] animate-glow-in rounded-full bg-sun/50 blur-3xl" style={{ animationDelay: "300ms" }} />
          </div>

          <BalanceCard stats={data} clinic={data.clinic} style={reveal(0)} onRecharge={() => setDialog(true)} />
          <StatCard
            i={1}
            icon={<Receipt size={18} strokeWidth={1.75} />}
            label="Total spent"
            value={data.spent}
            meta={data.spentIsNet ? `All time, net of ${data.refunded ? money(data.refunded) : "no"} refunds` : "All time, before refunds"}
            to="/wallet/transactions?type=deduction"
            toLabel="View deductions"
          />
          <StatCard
            i={2}
            icon={<Bot size={18} strokeWidth={1.75} />}
            label="Spent this month"
            value={data.monthSpent}
            meta={
              <div className="flex flex-col gap-0.5">
                <span>
                  {data.monthChange === null ? NO_DATA : `${data.monthChange > 0 ? "+" : ""}${data.monthChange}%`} vs same days in {prev}
                </span>
                <span>
                  {count(data.monthMessages)} messages · {data.monthAi ? count(data.monthAi.chats) : NO_DATA} AI chats · {data.monthAi ? count(Math.round((data.monthAi.call_seconds || 0) / 60)) : NO_DATA} call min
                </span>
              </div>
            }
            to="/wallet/transactions?range=month"
            toLabel="View this month's transactions"
          />

          <SpendChart />
          <RatesPanel pricing={data.pricing} aiRates={data.aiRates} />

          <section aria-labelledby="recent-title" style={reveal(5)} className={`${paper} relative col-span-12 flex animate-nav-in flex-col gap-2 p-6`}>
            <div className="flex items-center justify-between">
              <h2 id="recent-title" className="text-h3 font-semibold">
                Recent transactions
              </h2>
              <Link to="/wallet/transactions" className="flex h-9 items-center rounded-full px-4 text-small font-semibold text-ink ring-1 ring-line hover:bg-mist">
                View all
              </Link>
            </div>
            {data.recent.length === 0 && <p className="py-8 text-center text-small text-muted">No transactions yet. Recharges and charges appear here.</p>}
            <ul className="flex flex-col">
              {data.recent.map((t, i) => (
                <TxnRow key={t.id} t={t} i={i} fresh={t.type === "recharge" && t.ts >= freshSince} />
              ))}
            </ul>
          </section>
        </div>
      )}

      <RechargeDialog
        open={dialog}
        balance={data?.balance ?? null}
        daily={data?.daily ?? 0}
        billing={data?.billing ?? null}
        limits={data?.limits}
        onClose={close}
        onPaid={(amount) => {
          // The new recharge row glows once (window allows for server/browser clock drift).
          setFreshSince(Date.now() - 10 * 60_000);
          setToast(`${money(amount)} added to your wallet`);
          refresh();
        }}
        onPending={() => setTimeout(refresh, 4000)}
      />
      {toast && !dialog && <Toast message={toast} onDone={clearToast} />}
    </div>
  );
}
